import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { ReportErrorBoundary } from "@/components/reports/ReportErrorBoundary";
import { SuppressionSummary } from "./SuppressionSummary";
import { SuppressionActivity } from "./SuppressionActivity";
import { SuppressionCampaigns } from "./SuppressionCampaigns";
import { SuppressionHistory } from "./SuppressionHistory";

const isoDay = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
const daysAgo = (n) => isoDay(new Date(Date.now() - n * 86400000));

function SmsSuppressionReportInner() {
    const [range, setRange] = useState({ start: daysAgo(29), end: daysAgo(0) });
    const [state, setState] = useState({ loading: true, error: null, summary: null, activity: null, campaigns: null });

    const load = useCallback(async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const params = { start: range.start, end: range.end };
            const [s, a, c] = await Promise.all([
                api.get("/reports/sms-suppression/summary", { params }),
                api.get("/reports/sms-suppression/activity", { params }),
                api.get("/reports/sms-suppression/campaigns", { params }),
            ]);
            setState({ loading: false, error: null, summary: s.data, activity: a.data, campaigns: c.data });
        } catch (e) {
            setState((s) => ({ ...s, loading: false, error: formatApiError(e) }));
        }
    }, [range]);

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="space-y-6" data-testid="sms-suppression-report">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">SMS Suppression</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Blocking list, manual-review numbers, and campaign texts actually stopped before Twilio. Pacific dates.
                    </p>
                </div>
                <div className="flex items-end gap-2">
                    <div><Label className="text-xs">From</Label>
                        <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="supp-start-date" /></div>
                    <div><Label className="text-xs">To</Label>
                        <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="supp-end-date" /></div>
                    <Button onClick={load} disabled={state.loading || !range.start || !range.end} data-testid="supp-apply-btn">
                        {state.loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}Apply
                    </Button>
                </div>
            </div>
            {state.error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex gap-2" data-testid="supp-error">
                    <AlertTriangle className="h-4 w-4 mt-0.5" />{state.error}
                </div>
            )}
            {state.summary && <SuppressionSummary data={state.summary} />}
            {state.activity && <SuppressionActivity data={state.activity} />}
            {state.campaigns && <SuppressionCampaigns data={state.campaigns} />}
            <SuppressionHistory />
        </div>
    );
}

export default function SmsSuppressionReport() {
    return (
        <ReportErrorBoundary>
            <SmsSuppressionReportInner />
        </ReportErrorBoundary>
    );
}
